import { Lease, Unit } from '@urbanmix-tech/shared-js';
import dayjs, { Dayjs } from 'dayjs';
import { convertPseudoDateToRealDate } from '@/helpers/common';

export interface TrendsData {
  recentLeaseCount: number;
  recentAvgRent: number;
  historicalAvgRent: number;
  rentChange: number;
  rentChangePercent: number;
  recentAvgPPSF: number;
  historicalAvgPPSF: number;
  ppsfChange: number;
  ppsfChangePercent: number;
}

/**
 * Get lease start date safely
 */
function getLeaseStartDate(lease: Lease): Dayjs | null {
  const date = convertPseudoDateToRealDate(lease.leaseStartDate as any);
  if (date) {
    return dayjs(date);
  }
  return null;
}

/**
 * Analyze pricing trends comparing last 3 months to same period last year
 */
export function analyzeTrends(
  allLeases: Lease[],
  allUnits: Map<string, Unit>
): TrendsData | null {
  const now = dayjs();
  const threeMonthsAgo = now.subtract(3, 'month');
  const lastYearStart = now.subtract(1, 'year').subtract(3, 'month');
  const lastYearEnd = now.subtract(1, 'year');

  // Recent leases (last 3 months)
  const recentLeases = allLeases.filter((lease) => {
    const startDate = getLeaseStartDate(lease);
    if (!startDate) return false;
    return startDate.isAfter(threeMonthsAgo) && startDate.isBefore(now);
  });

  // Historical leases (same 3-month period last year)
  const historicalLeases = allLeases.filter((lease) => {
    const startDate = getLeaseStartDate(lease);
    if (!startDate) return false;
    return startDate.isAfter(lastYearStart) && startDate.isBefore(lastYearEnd);
  });

  if (recentLeases.length === 0 || historicalLeases.length === 0) {
    return null;
  }

  // Calculate metrics for recent leases
  const recentMetrics = recentLeases
    .map((lease) => {
      const unit = allUnits.get(lease.unitId);
      if (!unit) return null;

      const sqft = unit.sqft || 1;
      const annualRent = lease.monthlyRent * 12;
      const annualRentPerSqft = annualRent / sqft;

      return {
        rent: lease.monthlyRent,
        ppsf: annualRentPerSqft,
      };
    })
    .filter(Boolean) as any[];

  // Calculate metrics for historical leases
  const historicalMetrics = historicalLeases
    .map((lease) => {
      const unit = allUnits.get(lease.unitId);
      if (!unit) return null;

      const sqft = unit.sqft || 1;
      const annualRent = lease.monthlyRent * 12;
      const annualRentPerSqft = annualRent / sqft;

      return {
        rent: lease.monthlyRent,
        ppsf: annualRentPerSqft,
      };
    })
    .filter(Boolean) as any[];

  if (recentMetrics.length === 0 || historicalMetrics.length === 0) {
    return null;
  }

  const recentAvgRent =
    recentMetrics.reduce((sum, m) => sum + m.rent, 0) / recentMetrics.length;
  const historicalAvgRent =
    historicalMetrics.reduce((sum, m) => sum + m.rent, 0) /
    historicalMetrics.length;

  const recentAvgPPSF =
    recentMetrics.reduce((sum, m) => sum + m.ppsf, 0) / recentMetrics.length;
  const historicalAvgPPSF =
    historicalMetrics.reduce((sum, m) => sum + m.ppsf, 0) /
    historicalMetrics.length;

  const rentChange = recentAvgRent - historicalAvgRent;
  const rentChangePercent = (rentChange / historicalAvgRent) * 100;

  const ppsfChange = recentAvgPPSF - historicalAvgPPSF;
  const ppsfChangePercent = (ppsfChange / historicalAvgPPSF) * 100;

  return {
    recentLeaseCount: recentLeases.length,
    recentAvgRent,
    historicalAvgRent,
    rentChange,
    rentChangePercent,
    recentAvgPPSF,
    historicalAvgPPSF,
    ppsfChange,
    ppsfChangePercent,
  };
}
